import { router } from "@inertiajs/react"
import { Copy, Info } from "lucide-react"
import { toast } from "sonner"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import type {
  IssueSeverity,
  QualityIssueActions,
  QualityIssueCard,
  QualitySuggestedFix,
} from "@/lib/types"

interface Props {
  issue: QualityIssueCard
  actions: QualityIssueActions
  selected: boolean
  onToggleSelect: (id: number) => void
}

function SeverityBadge({ severity }: { severity: IssueSeverity }) {
  const config: Record<IssueSeverity, { label: string; className: string }> = {
    critical: {
      label: "Critical",
      className: "bg-destructive/10 text-destructive border-destructive/30",
    },
    high: {
      label: "High",
      className:
        "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30",
    },
    medium: {
      label: "Medium",
      className: "bg-muted text-muted-foreground border-border",
    },
  }
  const { label, className } = config[severity]
  return (
    <Badge variant="outline" className={className} aria-label={`Severity: ${label}`}>
      <Info className="size-3" aria-hidden />
      <span>{label}</span>
    </Badge>
  )
}

export function DuplicateCard({ issue, actions, selected, onToggleSelect }: Props) {
  const fix: QualitySuggestedFix = issue.suggested_fix ?? {}
  const dupId = fix.duplicate_of_poi_id
  const sim = typeof fix.similarity === "number" ? Math.round(fix.similarity * 100) : null

  const handleDismiss = () => {
    router.post(
      actions.dismiss,
      { reason: "out_of_scope" },
      {
        preserveScroll: true,
        onSuccess: () => toast("Duplicate dismissed"),
      },
    )
  }

  return (
    <Card
      data-issue-id={issue.id}
      data-issue-type="duplicate"
      className={selected ? "ring-2 ring-primary/40" : undefined}
    >
      <CardContent className="space-y-3 py-4">
        <div className="flex items-center gap-2">
          <SeverityBadge severity={issue.severity} />
          <Badge variant="secondary">
            <Copy className="size-3" aria-hidden />
            <span>Duplicate</span>
          </Badge>
        </div>

        <div>
          <div className="text-sm font-medium">
            Possible duplicate:{" "}
            {issue.subject_name ?? `POI #${issue.subject_id}`}
            {dupId && (
              <>
                <span className="mx-1 text-muted-foreground">↔</span>
                <span>POI #{dupId}</span>
              </>
            )}
          </div>
          {sim !== null && (
            <p className="mt-1 text-sm text-muted-foreground">
              Name similarity: {sim}%
            </p>
          )}
          <p className="mt-2 text-xs text-muted-foreground">
            Merge workflow is part of a future release. For now you can dismiss this
            detection or keep both records.
          </p>
        </div>

        <div className="flex items-center justify-between gap-2 pt-2">
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <Checkbox
              checked={selected}
              onCheckedChange={() => onToggleSelect(issue.id)}
              aria-label="Select duplicate issue for batch action"
            />
            <span>Select</span>
          </label>

          <Button
            size="sm"
            variant="ghost"
            onClick={handleDismiss}
            className="min-h-9"
          >
            Dismiss
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
